import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { AgentTurnGap } from '@avisoma/shared';
import CopyButton from '../../components/CopyButton';
import { ID } from '../../components/ID';
import { Link } from '../../components/Link';
import { TableSkeleton } from '../../components/Skeleton';
import { SortableTable, type SortableTableColumn } from '../../components/SortableTable';
import { formatDateTime, formatRelativeTime } from '../../lib/dateFormat';
import backend from '../../services/backend';
import { hasActiveFilters, toFilterQuery, type ChatQuestionsFilters } from './chatQuestionsFilters';

const columns: SortableTableColumn<AgentTurnGap>[] = [
  {
    label: 'Gap',
    accessor: 'gap',
    render: (row) => (
      <span className="min-w-48 whitespace-normal line-clamp-2" title={row.gap}>
        {row.gap}
      </span>
    ),
  },
  {
    label: 'Count',
    accessor: 'count',
    sortable: true,
    render: (row) => <span className="tabular-nums">{row.count}</span>,
  },
  {
    label: 'Last seen',
    accessor: 'lastAt',
    sortable: true,
    render: (row) => (
      <span className="text-gray-500 dark:text-zinc-400" title={formatDateTime(row.lastAt)}>
        {formatRelativeTime(row.lastAt)}
      </span>
    ),
  },
  {
    label: 'Example',
    accessor: 'sampleQuestion',
    render: (row) => (
      <span className="flex items-center gap-3">
        <span
          className="min-w-40 whitespace-normal line-clamp-2 text-gray-500 dark:text-zinc-400"
          title={row.sampleQuestion}
        >
          {row.sampleQuestion}
        </span>
        {/* Stop the click here so following the project link does not also open the gap's turns. */}
        <span className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Link
            to={`/projects/${row.sampleProjectId}`}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <ID id={row.sampleProjectId} />
          </Link>
          <CopyButton text={row.sampleProjectId} />
        </span>
      </span>
    ),
  },
];

export default function GapsTab({
  filters,
  onSelectGap,
}: {
  filters: ChatQuestionsFilters;
  onSelectGap: (gapKey: string) => void;
}) {
  const gapsQuery = useQuery({
    queryKey: ['agent-turn-gaps', filters],
    queryFn: () => backend.listAgentTurnGaps(toFilterQuery(filters)).then((res) => res.data),
    placeholderData: keepPreviousData,
  });

  const items = gapsQuery.data?.items ?? [];

  if (gapsQuery.isLoading) return <TableSkeleton />;

  if (gapsQuery.error) {
    return (
      <div className="text-red-600 dark:text-red-400 text-center text-sm pt-12">
        {(gapsQuery.error as Error).message}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 overflow-hidden transition-opacity',
        gapsQuery.isPlaceholderData && 'opacity-50 pointer-events-none',
      )}
    >
      {items.length > 0 ? (
        <SortableTable
          columns={columns}
          data={items}
          rowKey={(row) => row.gapKey}
          storageId="chat-questions-gaps"
          defaultSortBy="count"
          defaultSortDir="desc"
          limitHeight={false}
          onRowClick={(row) => onSelectGap(row.gapKey)}
        />
      ) : (
        <p className="p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
          {hasActiveFilters(filters) ? 'No gaps match these filters.' : 'No gaps recorded yet.'}
        </p>
      )}
    </div>
  );
}
